import { RiToolsFill } from "@itandi/itandi-bb-ui";
import type React from "react";
import { EditMenuButton } from "~/components/common/parts/button/EditMenuButton";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { Deal } from "~/models/Deal";
import { FINISHED, OWN_FINISHED } from "~/models/Status";
import { getModelNumberFilePath as clientNumberFilePath } from "~/utils/api/deals/DealRepair";
import { getModelNumberFilePath as vendorNumberFilePath } from "~/utils/api/vendors/deals/DealRepair";
import { isEmpty } from "~/utils/StringUtil";
import Styles from "../DealCard.module.scss";
import { DealRepairCardModal } from "./DealRepairCardModal";

type Props = {
	companyType: "client" | "vendor";
	deal: Deal;
	handleMutate?: (() => Promise<void>) | null;
};

const renderNotYetInput = (): React.ReactNode => (
	<span className={Styles.NotYet}>未入力</span>
);

export function DealRepairCard({
	deal,
	companyType,
	handleMutate = null
}: Props) {
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const repair = deal.dealRepair;

	if (repair === null) {
		return null;
	}

	return (
		<div className={Styles.DealCard}>
			<h3 className={Styles.Title}>
				<RiToolsFill className={Styles.TitleIcon} />
				修繕情報
				{companyType === "client" && (
					<div className={Styles.EditButtonWrapper}>
						{deal.statusId !== FINISHED && deal.statusId !== OWN_FINISHED && (
							<EditMenuButton onClick={handleOpen}>変更</EditMenuButton>
						)}
						<DealRepairCardModal
							deal={deal}
							handleClose={handleClose}
							handleMutate={handleMutate}
							isOpen={isOpen}
						/>
					</div>
				)}
			</h3>
			<div className={Styles.Content}>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>依頼内容</dt>
					<dd className={Styles.ListContentValue}>
						<p className={Styles.longSentence}>
							{isEmpty(repair.requestContent)
								? renderNotYetInput()
								: repair.requestContent}
						</p>
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>カテゴリー</dt>
					<dd className={Styles.ListContentValue}>
						<p>
							{isEmpty(repair.itemCategory)
								? renderNotYetInput()
								: repair.itemCategory}
						</p>
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>商品名</dt>
					<dd className={Styles.ListContentValue}>
						<p>
							{isEmpty(repair.itemName) ? renderNotYetInput() : repair.itemName}
						</p>
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>メーカー</dt>
					<dd className={Styles.ListContentValue}>
						<p>
							{isEmpty(repair.manufacturer)
								? renderNotYetInput()
								: repair.manufacturer}
						</p>
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>型番</dt>
					<dd className={Styles.ListContentValue}>
						<p>
							{isEmpty(repair.modelNumber)
								? renderNotYetInput()
								: repair.modelNumber}
						</p>
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>現地調査希望</dt>
					<dd className={Styles.ListContentValue}>
						{deal.conductFieldSurvey ? "あり" : "なし"}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>型番画像</dt>
					<dd className={Styles.ListContentValue}>
						{isEmpty(repair.modelNumberFile) ? (
							renderNotYetInput()
						) : (
							<a
								className={Styles.Link}
								href={
									companyType === "client"
										? clientNumberFilePath(deal.id)
										: vendorNumberFilePath(deal.id)
								}
								rel="noreferrer"
								target="_blank"
							>
								型番画像
							</a>
						)}
					</dd>
				</dl>
			</div>
		</div>
	);
}
